import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { KTSVG, SimpleAlert } from '../../../../rbts/helpers';
import {
  crearCodigoPromocionalService,
  actualizarCodigoPromocionalService,
} from '../../../service/promocionales/CodigosPromocionales';

type Props = {
  show: boolean;
  handleClose: (refresh: boolean) => void;
  registro: any | null;
};

const codigoSchema = Yup.object().shape({
  codigo: Yup.string()
    .trim()
    .required('El código es requerido.')
    .max(30, 'Máximo 30 caracteres.'),
  tipo_descuento: Yup.string()
    .oneOf(['PORCENTAJE', 'MONTO'])
    .required('El tipo de descuento es requerido.'),
  valor: Yup.number()
    .moreThan(0, 'El valor debe ser mayor a 0.')
    .required('El valor es requerido.'),
  vigencia_fin: Yup.date()
    .nullable()
    .min(Yup.ref('vigencia_inicio'), 'No puede ser menor a la vigencia inicio.'),
  limite_usos: Yup.number().min(0, 'No puede ser negativo.'),
});

const initialValues = {
  codigo: '',
  descripcion: '',
  tipo_descuento: 'PORCENTAJE',
  valor: 0,
  vigencia_inicio: '',
  vigencia_fin: '',
  limite_usos: 0,
  acumulable: false,
};

const ModalCRU: React.FC<Props> = ({ show, handleClose, registro }) => {
  const [loading, setLoading] = useState(false);
  const isEditing = !!registro?.id;

  const formCodigo = useFormik({
    initialValues,
    validationSchema: codigoSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          ...values,
          codigo: values.codigo.trim().toUpperCase(),
          tipo_descuento: values.tipo_descuento as 'PORCENTAJE' | 'MONTO',
          vigencia_inicio: values.vigencia_inicio || null,
          vigencia_fin: values.vigencia_fin || null,
        };
        if (isEditing) {
          await actualizarCodigoPromocionalService(registro.id, payload);
          SimpleAlert('Código actualizado correctamente', 3000, 'success');
        } else {
          await crearCodigoPromocionalService(payload);
          SimpleAlert('Código creado correctamente', 3000, 'success');
        }
        formCodigo.resetForm();
        handleClose(true);
      } catch (error: any) {
        if (error && error.response) {
          const { message } = error.response.data;
          SimpleAlert(message, 3000, 'error');
        }
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (registro?.id) {
      formCodigo.setValues({
        codigo: registro.codigo ?? '',
        descripcion: registro.descripcion ?? '',
        tipo_descuento: registro.tipo_descuento ?? 'PORCENTAJE',
        valor: Number(registro.valor) || 0,
        vigencia_inicio: registro.vigencia_inicio
          ? new Date(registro.vigencia_inicio).toISOString().split('T')[0]
          : '',
        vigencia_fin: registro.vigencia_fin
          ? new Date(registro.vigencia_fin).toISOString().split('T')[0]
          : '',
        limite_usos: registro.limite_usos ?? 0,
        acumulable: !!registro.acumulable,
      });
    } else {
      formCodigo.setValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registro, show]);

  return (
    <Modal
      id="kt_modal_codigo_promocional"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered mw-600px"
      show={show}
      onHide={() => handleClose(false)}
    >
      <div className="modal-header">
        <h2>{isEditing ? 'Editar código promocional' : 'Nuevo código promocional'}</h2>
        <div
          className="btn btn-sm btn-icon btn-active-color-primary"
          onClick={() => handleClose(false)}
        >
          <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
        </div>
      </div>
      <div className="modal-body scroll-y mx-5">
        <form onSubmit={formCodigo.handleSubmit}>
          <div className="row mb-5">
            <div className="col-md-6">
              <label className="required form-label fw-bolder">Código</label>
              <input
                type="text"
                className="form-control form-control-solid text-uppercase"
                disabled={isEditing}
                {...formCodigo.getFieldProps('codigo')}
              />
              {formCodigo.touched.codigo && formCodigo.errors.codigo && (
                <div className="text-danger fs-7 mt-1">{formCodigo.errors.codigo}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bolder">Descripción</label>
              <input
                type="text"
                className="form-control form-control-solid"
                {...formCodigo.getFieldProps('descripcion')}
              />
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-6">
              <label className="required form-label fw-bolder">Tipo de descuento</label>
              <select
                className="form-select form-select-solid"
                {...formCodigo.getFieldProps('tipo_descuento')}
              >
                <option value="PORCENTAJE">Porcentaje (%)</option>
                <option value="MONTO">Monto fijo ($)</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="required form-label fw-bolder">Valor</label>
              <input
                type="number"
                min={0}
                step="0.01"
                className="form-control form-control-solid"
                {...formCodigo.getFieldProps('valor')}
              />
              {formCodigo.touched.valor && formCodigo.errors.valor && (
                <div className="text-danger fs-7 mt-1">{formCodigo.errors.valor}</div>
              )}
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-6">
              <label className="form-label fw-bolder">Vigencia inicio</label>
              <input
                type="date"
                className="form-control form-control-solid"
                {...formCodigo.getFieldProps('vigencia_inicio')}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bolder">Vigencia fin</label>
              <input
                type="date"
                className="form-control form-control-solid"
                {...formCodigo.getFieldProps('vigencia_fin')}
              />
              {formCodigo.touched.vigencia_fin && formCodigo.errors.vigencia_fin && (
                <div className="text-danger fs-7 mt-1">{formCodigo.errors.vigencia_fin}</div>
              )}
            </div>
          </div>
          <div className="row mb-5">
            <div className="col-md-6">
              <label className="form-label fw-bolder">Límite de usos (0 = ilimitado)</label>
              <input
                type="number"
                min={0}
                className="form-control form-control-solid"
                {...formCodigo.getFieldProps('limite_usos')}
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="form-check form-switch form-check-custom form-check-solid mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formCodigo.values.acumulable}
                  onChange={(e) =>
                    formCodigo.setFieldValue('acumulable', e.target.checked)
                  }
                />
                <label className="form-check-label fw-bolder">Acumulable</label>
              </div>
            </div>
          </div>
          <div className="text-end pt-5">
            <button
              type="button"
              className="btn btn-light me-3"
              onClick={() => handleClose(false)}
              disabled={loading}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {!loading ? (
                <span className="indicator-label">Guardar</span>
              ) : (
                <span className="indicator-progress" style={{ display: 'block' }}>
                  Guardando...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export { ModalCRU };
